import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CommentHighlight } from "./CommentHighlight";
// Import statement removed - defining interface locally
import { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { DatabaseService } from "../services/database";
import CommentDisplay from "./CommentDisplay";
import stripePromise from "../lib/stripe";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import { usePostHog } from "../contexts/PostHogProvider";
import { ScoreReportDisplay } from "./ScoreReport";
import { Essay, EssayShare, PublicShareLink } from "../types/database";
import EssaySettingsModal from "./EssaySettingsModal";

// Utility function to check if a string is a valid UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export type Analysis = {
  timestamp: string;
  name: string;
  prompt: string;
  content: string;
  comments: Comment[];
  summary: string;
  report: ScoreReport | null;
};

export interface EssayEditorProps {
  initialContent?: string;
  initialPrompt?: string;
  initialTitle?: string;
  initialComments?: Comment[];
  initialSummary?: string;
  initialReport?: ScoreReport | null;
  analysisHistory?: Analysis[];
  wordLimit?: number;
  onSave?: (
    content: string,
    comments: Comment[],
    summary: string,
    prompt: string,
    title: string,
    report: ScoreReport | null
  ) => Promise<void>;
  onAnalysisSave?: (
    content: string,
    comments: Comment[],
    summary: string,
    prompt: string,
    title: string,
    report: ScoreReport | null
  ) => Promise<void>;
  onEssayOnlySave?: (
    content: string,
    prompt: string,
    title: string
  ) => Promise<void>;
  onSaveTitle?: (title: string) => Promise<void>;
  onDeleteAnalysis?: (index: number) => void;
  onRenameAnalysis?: (index: number, newName: string) => void;
  onUnsavedChanges?: (hasChanges: boolean) => void;
  onClose?: () => void;
  essayId?: string;
  collegeName?: string;
  overridePermission?: string;
}

export interface ScoreReport {
  clarity_and_focus: number;
  structure_and_organization: number;
  grammar_and_mechanics: number;
  word_choice_and_tone: number;
  persuasiveness: number;
  creativity_and_originality: number;
  overall_score: number;
}

export interface UserComment {
  id: string;
  text: string;
  position: number;
  endPosition?: number;
  selectedText?: string;
  createdAt: Date;
  type: "user";
  author?: string;
  finalized?: boolean;
}

export interface AIComment {
  id: string;
  phrase: string;
  comment: string;
  type: "ai";
}

type Comment = UserComment | AIComment;

// Custom Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
  description?: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  menuWidth?: number; // optional: pixel width for portal menu
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Select an option",
  className = "",
  menuWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideTrigger = dropdownRef.current?.contains(targetNode);
      const clickedInsideMenu = menuRef.current?.contains(targetNode);
      if (!clickedInsideTrigger && !clickedInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const triggerEl = dropdownRef.current;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const desiredWidth = menuWidth ?? rect.width;
    const viewportWidth = window.innerWidth;
    const left = Math.min(
      Math.max(rect.left, 8),
      Math.max(8, viewportWidth - desiredWidth - 8)
    );
    setMenuPosition({
      top: rect.bottom + 4,
      left,
      width: Math.min(desiredWidth, viewportWidth - 16),
    });
  }, [menuWidth]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    const onResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, updateMenuPosition]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full h-9 px-3 text-sm font-medium text-left bg-white border border-gray-200 rounded-lg
          flex items-center justify-between gap-2 min-w-[140px]
          ${
            disabled
              ? "text-gray-400 cursor-not-allowed bg-gray-50"
              : "text-gray-700 hover:bg-gray-50 hover:border-gray-300 cursor-pointer"
          }
          ${isOpen ? "border-blue-500 ring-1 ring-blue-500" : ""}
          transition-all duration-150
        `}
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[60] max-h-60 overflow-y-auto"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className={`
                  w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors
                  ${
                    value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-700"
                  }
                  ${options.indexOf(option) === 0 ? "rounded-t-lg" : ""}
                  ${
                    options.indexOf(option) === options.length - 1
                      ? "rounded-b-lg"
                      : ""
                  }
                `}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{option.label}</span>
                  {option.description && (
                    <span className="text-xs text-gray-600 mt-0.5 whitespace-normal leading-5">
                      {option.description}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

// Idea Creation Component
// Module-level caches to avoid refetching across tab switches/remounts
const promptTipsCache = new Map<string, string[]>();
const inFlightTips = new Set<string>();
interface IdeaCreationProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  disabled?: boolean;
  userId?: string;
  // Suggestions props
  suggestions: {
    angle: string;
    how_to_use: string;
    example_sentence?: string;
  }[];
  onSuggestionsChange: (
    suggestions: {
      angle: string;
      how_to_use: string;
      example_sentence?: string;
    }[]
  ) => void;
  // Tips props - passed from parent
  tips: string[];
  tipsLoading: boolean;
  tipsError: string;
  onTipsChange: (tips: string[]) => void;
  // Chat props
  chatOpen: boolean;
  chatMessages: Array<{ role: "user" | "assistant"; content: string }>;
  chatInput: string;
  chatLoading: boolean;
  onOpenChat: () => void;
  onCloseChat: () => void;
  onSendMessage: (suggestions?: any[]) => void;
  onChatInputChange: (input: string) => void;
}

const IdeaCreation: React.FC<IdeaCreationProps> = ({
  prompt,
  onPromptChange,
  disabled = false,
  userId,
  // Suggestions props
  suggestions,
  onSuggestionsChange,
  // Tips props
  tips,
  tipsLoading,
  tipsError,
  onTipsChange,
  // Chat props
  chatOpen,
  chatMessages,
  chatInput,
  chatLoading,
  onOpenChat,
  onCloseChat,
  onSendMessage,
  onChatInputChange,
}) => {
  const [ideas, setIdeas] = useState<string[]>([]);
  const [currentIdea, setCurrentIdea] = useState("");
  const [ideationLoading, setIdeationLoading] = useState(false);
  const [ideationError, setIdeationError] = useState<string>("");
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [promptTipsExpanded, setPromptTipsExpanded] = useState(false);
  const suggestionsTopRef = useRef<HTMLDivElement | null>(null);
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const [sessions, setSessions] = useState<
    {
      id: string;
      prompt: string;
      ideas: string[];
      tips: string[];
      suggestions: any[];
      created_at: string;
    }[]
  >([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadedFromDB, setLoadedFromDB] = useState(false);
  const sessionMenuRef = useRef<HTMLDivElement | null>(null);

  // Close sessions dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!sessionMenuOpen) return;
      if (
        sessionMenuRef.current &&
        !sessionMenuRef.current.contains(e.target as Node)
      ) {
        setSessionMenuOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSessionMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [sessionMenuOpen]);

  const handleAddIdea = () => {
    if (currentIdea.trim() && !ideas.includes(currentIdea.trim())) {
      setIdeas([...ideas, currentIdea.trim()]);
      setCurrentIdea("");
    }
  };

  const handleRemoveIdea = (index: number) => {
    setIdeas(ideas.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddIdea();
    }
  };

  // On mount (or when prompt changes), try to hydrate from DB to persist across tab switches
  useEffect(() => {
    let isMounted = true;
    async function hydrateFromDB() {
      if (loadedFromDB) return;
      try {
        const userId = (window as any).__EP_USER_ID__ || undefined;
        if (userId && prompt && prompt.trim().length >= 8) {
          const url =
            API_ENDPOINTS.getBrainstormSessions(userId, 1) +
            `&prompt=${encodeURIComponent(prompt)}`;
          const res = await axios.get(url);
          const row =
            Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : null;
          if (row && isMounted) {
            onTipsChange(Array.isArray(row.tips) ? row.tips : []);
            setIdeas(Array.isArray(row.ideas) ? row.ideas : []);
            onSuggestionsChange(
              Array.isArray(row.suggestions) ? row.suggestions : []
            );
            setLoadedFromDB(true);
          }
        }
      } catch (e) {
        // Ignore hydration errors silently for now
      }
    }
    hydrateFromDB();
    return () => {
      isMounted = false;
    };
  }, [prompt, loadedFromDB, onSuggestionsChange]);

  const handleIdeate = async () => {
    if (!prompt.trim() || ideas.length === 0) return;
    try {
      setIdeationLoading(true);
      setIdeationError("");
      const res = await axios.post(API_ENDPOINTS.ideate, {
        prompt,
        ideas,
        model: "gpt-5-mini",
        user_id: userId,
      });
      const newSuggestions = Array.isArray(res.data.suggestions)
        ? res.data.suggestions
        : [];
      onSuggestionsChange(newSuggestions);
    } catch (e) {
      setIdeationError("Failed to ideate. Please try again.");
    } finally {
      setIdeationLoading(false);
    }
  };

  useEffect(() => {
    if (suggestions.length > 0) {
      const el = suggestionsTopRef.current;
      if (el) {
        const navbarOffset = -15; // approximate navbar offset used in Navbar
        const rectTop = el.getBoundingClientRect().top;
        const targetTop = rectTop + window.pageYOffset - navbarOffset;
        window.scrollTo({ top: targetTop, behavior: "smooth" });
      }
    }
  }, [suggestions.length]);

  return (
    <div className="relative">
      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          chatOpen ? "mr-[max(calc(33.33333%+1rem),380px)]" : "w-full"
        }`}
      >
        <div className="space-y-6">
          {/* Essay Prompt Section */}
          <div className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-visible">
            <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Essay Prompt
              </h3>
              {/* Past Sessions Dropdown */}
              <div className="relative" ref={sessionMenuRef}>
                <button
                  type="button"
                  onClick={async () => {
                    setSessionMenuOpen((o) => !o);
                    if (!sessionMenuOpen && sessions.length === 0) {
                      try {
                        setSessionsLoading(true);
                        if (userId) {
                          const res = await axios.get(
                            API_ENDPOINTS.getBrainstormSessions(userId, 20)
                          );
                          setSessions(res.data || []);
                        }
                      } catch (e) {
                        // silent fail for now
                      } finally {
                        setSessionsLoading(false);
                      }
                    }
                  }}
                  className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  Past Sessions
                </button>
                {sessionMenuOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[190px] overflow-y-auto overscroll-contain">
                    <div className="p-2 border-b text-xs text-gray-500">
                      Most recent
                    </div>
                    {sessionsLoading ? (
                      <div className="p-3 text-sm text-gray-500">
                        Loading...
                      </div>
                    ) : sessions.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">
                        No past sessions
                      </div>
                    ) : (
                      sessions.map((s) => (
                        <button
                          key={s.id}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50"
                          onClick={() => {
                            // Load session into UI
                            onTipsChange(Array.isArray(s.tips) ? s.tips : []);
                            const restoredIdeas = Array.isArray(s.ideas)
                              ? s.ideas
                              : [];
                            setIdeas(restoredIdeas);
                            const restoredSuggestions = Array.isArray(
                              s.suggestions
                            )
                              ? s.suggestions
                              : [];
                            onSuggestionsChange(restoredSuggestions as any);
                            // Close menu and scroll to suggestions if present
                            setSessionMenuOpen(false);
                            if (restoredSuggestions.length > 0) {
                              setTimeout(() => {
                                const el = suggestionsTopRef.current;
                                if (el) {
                                  const navbarOffset = -15;
                                  const rectTop =
                                    el.getBoundingClientRect().top;
                                  const targetTop =
                                    rectTop + window.pageYOffset - navbarOffset;
                                  window.scrollTo({
                                    top: targetTop,
                                    behavior: "smooth",
                                  });
                                }
                              }, 0);
                            }
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900 line-clamp-1">
                            {s.prompt || "Untitled session"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(s.created_at).toLocaleString()}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 pt-3 pb-3">
              <textarea
                className="w-full border-none focus:ring-0 resize-none text-base transition-colors duration-200 ease-in-out focus:outline-none"
                placeholder="Enter the essay prompt (e.g., 'Why do you want to attend our college?')"
                rows={2}
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* Expandable Prompt-Specific Tips Section */}
            {(tipsLoading || tips.length > 0 || tipsError) && (
              <div className="border-t border-gray-100">
                {/* Tips Header/Toggle */}
                <button
                  onClick={() => setPromptTipsExpanded(!promptTipsExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
                  disabled={tipsLoading}
                >
                  <div className="flex items-center gap-2">
                    {tipsLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-blue-600 font-medium">
                          Analyzing prompt...
                        </span>
                      </>
                    ) : tips.length > 0 ? (
                      <>
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                          />
                        </svg>
                        <span className="text-sm text-blue-600 font-medium">
                          {tips.length} {tips.length === 1 ? "tip" : "tips"}{" "}
                          available
                        </span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <span className="text-sm text-gray-500">
                          {tipsError}
                        </span>
                      </>
                    )}
                  </div>
                  {!tipsLoading && tips.length > 0 && (
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${
                        promptTipsExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </button>

                {/* Expanded Tips Content */}
                {promptTipsExpanded && tips.length > 0 && (
                  <div className="px-4 pb-4 pt-2">
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      <ul className="text-sm text-blue-800 space-y-2 list-disc pl-5">
                        {tips.map((t, idx) => (
                          <li key={idx}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Idea Collection Section */}
          <div className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 11H6l3-9 3 9h-3z" />
                    <path d="M20 12h-6l6 9-6-9z" />
                  </svg>
                  Brainstorm Ideas
                </h3>
                <button
                  onClick={() => setShowTipsModal(true)}
                  className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full hover:bg-blue-50"
                  title="View brainstorming tips"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Enter words, phrases, or concepts relevant to your prompt. Think
                about specific experiences, values, or unique aspects.
              </p>
              <p className="text-xs text-gray-500 mt-1 italic">
                Example: "volunteering at animal shelter", "overcoming stage
                fright", "cultural identity", "leadership in debate team"
              </p>
            </div>

            <div className="p-4 space-y-4">
              {/* Input for new ideas */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentIdea}
                  onChange={(e) => setCurrentIdea(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter an idea, word, or phrase..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                  disabled={disabled}
                />
                <button
                  onClick={handleAddIdea}
                  disabled={!currentIdea.trim() || disabled}
                  className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 rounded-lg transition-colors text-sm font-medium"
                >
                  Add
                </button>
                <button
                  onClick={handleIdeate}
                  disabled={disabled || ideas.length === 0 || !prompt.trim()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    ideationLoading
                      ? "bg-purple-100 text-purple-400 cursor-not-allowed"
                      : disabled || ideas.length === 0 || !prompt.trim()
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-purple-50 text-purple-600 hover:bg-purple-100"
                  }`}
                >
                  {ideationLoading ? "Brainstorming..." : "Brainstorm"}
                </button>
              </div>

              {/* Display added ideas */}
              {ideas.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Your Ideas:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {ideas.map((idea, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200"
                      >
                        <span>{idea}</span>
                        <button
                          onClick={() => handleRemoveIdea(index)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          disabled={disabled}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {suggestions.length > 0 && (
                <div className="mt-6" ref={suggestionsTopRef}>
                  <div className="p-4 border border-purple-200 bg-purple-50/60 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-purple-800">
                        ✨ Ways to use your ideas
                      </h4>
                      <button
                        onClick={onOpenChat}
                        className="editor-toolbar-button bg-purple-50 text-purple-600 hover:bg-purple-100"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Chat to Explore
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 max-h-[500px] overflow-y-auto pr-2">
                      {suggestions.map((s, i) => (
                        <div
                          key={i}
                          className="rounded-lg bg-white border border-purple-100 p-3 shadow-sm"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {s.angle}
                          </div>
                          <div className="text-sm text-gray-700 mt-1">
                            {s.how_to_use}
                          </div>
                          {s.example_sentence && (
                            <div className="text-sm text-gray-500 mt-2 italic">
                              "{s.example_sentence}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {ideationError && (
                <div className="text-sm text-red-600">{ideationError}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Sidebar */}
      {chatOpen && (
        <div className="fixed right-0 top-0 w-[min(33.33333%,calc(100vw-380px))] min-w-[360px] h-screen bg-white border-l border-gray-100 z-40 pt-20">
          <div className="h-full flex flex-col">
            {/* Clean Chat Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Chat Assistant
                  </h3>
                  <p className="text-gray-500 text-sm mt-0.5">
                    Explore your ideas
                  </p>
                </div>
                <button
                  onClick={onCloseChat}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 bg-gray-50/30">
              {chatMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 ${
                      message.role === "user"
                        ? "bg-blue-500 text-white rounded-2xl rounded-br-lg"
                        : "bg-white text-gray-700 rounded-2xl rounded-bl-lg border border-gray-200"
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.content}</p>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-700 rounded-2xl rounded-bl-lg px-4 py-3 border border-gray-200">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.15s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.3s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Clean Chat Input */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex gap-3 items-center">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => onChatInputChange(e.target.value)}
                    placeholder="Type a message here"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder:text-gray-400"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        onSendMessage(suggestions);
                      }
                    }}
                    disabled={chatLoading}
                  />
                </div>
                <button
                  onClick={() => onSendMessage(suggestions)}
                  disabled={chatLoading || !chatInput.trim()}
                  className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {chatLoading ? (
                    <div className="w-5 h-5 animate-spin">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v4m0 8v4m-8-8h4m8 0h4"
                        />
                      </svg>
                    </div>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Brainstorming Tips Modal */}
      {showTipsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTipsModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Brainstorming Tips
              </h2>
              <button
                onClick={() => setShowTipsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* General Brainstorming Tips */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                <h3 className="text-base font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <span>💡</span>
                  General Tips
                </h3>
                <ul className="text-sm text-amber-700 space-y-2 list-disc pl-5">
                  <li>
                    Think about specific experiences, not general statements
                  </li>
                  <li>Include unique details that only apply to you</li>
                  <li>Consider both obvious and unexpected connections</li>
                  <li>
                    Don't worry about perfect wording — just capture your
                    thoughts
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary Section Component with three specific collapsible sections
const SummarySection: React.FC<{ summary: string }> = ({ summary }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["strengths"])
  ); // Strengths expanded by default

  const toggleSection = (sectionKey: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey);
    } else {
      newExpanded.add(sectionKey);
    }
    setExpandedSections(newExpanded);
  };

  // Categorize content into the three main sections
  const categorizeContent = (summary: string) => {
    const sections = summary.split("\n\n").filter((section) => section.trim());
    const categorized = {
      strengths: [] as string[],
      improvements: [] as string[],
      opportunities: [] as string[],
    };

    sections.forEach((section) => {
      const [heading, ...content] = section.split("\n");
      const lowerHeading = heading
        .toLowerCase()
        .replace(/[✨🎯📝]/g, "")
        .trim();
      const cleanContent = content
        .filter((line) => line.trim())
        .map((line) => line.replace(/^[•-]\s*/, "").trim())
        .filter((line) => line.length > 0);

      // Categorization into three sections
      if (
        lowerHeading.includes("strength") ||
        lowerHeading.includes("positive") ||
        lowerHeading.includes("good") ||
        lowerHeading.includes("well") ||
        lowerHeading.includes("effective") ||
        lowerHeading.includes("strong") ||
        lowerHeading.includes("excellent") ||
        lowerHeading.includes("compelling") ||
        lowerHeading.includes("clear") ||
        lowerHeading.includes("engaging")
      ) {
        categorized.strengths.push(...cleanContent);
      } else if (
        lowerHeading.includes("suggestion") ||
        lowerHeading.includes("recommendation") ||
        lowerHeading.includes("opportunity") ||
        lowerHeading.includes("competitive") ||
        lowerHeading.includes("edge") ||
        lowerHeading.includes("enhance") ||
        lowerHeading.includes("consider") ||
        lowerHeading.includes("could") ||
        lowerHeading.includes("might") ||
        lowerHeading.includes("potential") ||
        lowerHeading.includes("next steps") ||
        lowerHeading.includes("moving forward")
      ) {
        categorized.opportunities.push(...cleanContent);
      } else {
        // Everything else goes to improvements (areas for improvement, weaknesses, etc.)
        categorized.improvements.push(...cleanContent);
      }
    });

    return categorized;
  };

  const categorizedContent = categorizeContent(summary);

  const sectionConfigs = [
    {
      key: "strengths",
      title: "Strengths",
      content: categorizedContent.strengths,
      colors: "text-emerald-700 bg-emerald-50 border-emerald-200",
      icon: (
        <svg
          className="w-4 h-4 text-emerald-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      key: "improvements",
      title: "Areas for Improvement",
      content: categorizedContent.improvements,
      colors: "text-amber-700 bg-amber-50 border-amber-200",
      icon: (
        <svg
          className="w-4 h-4 text-amber-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      key: "opportunities",
      title: "Competitive Edge Opportunities",
      content: categorizedContent.opportunities,
      colors: "text-blue-700 bg-blue-50 border-blue-200",
      icon: (
        <svg
          className="w-4 h-4 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {sectionConfigs.map((config) => {
        if (config.content.length === 0) return null; // Don't show empty sections

        const isExpanded = expandedSections.has(config.key);

        return (
          <div
            key={config.key}
            className="border border-gray-100 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-sm"
          >
            <button
              onClick={() => toggleSection(config.key)}
              className={`w-full px-4 py-3 text-left flex items-center justify-between transition-all duration-200 hover:bg-opacity-80 ${config.colors}`}
            >
              <div className="flex items-center gap-3">
                {config.icon}
                <h5 className="font-semibold text-sm">{config.title}</h5>
                <span className="text-xs opacity-70">
                  ({config.content.length})
                </span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 bg-white">
                <div className="space-y-2 mt-2">
                  {config.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-1 h-1 bg-gray-400 rounded-full mt-2"></div>
                      <p className="text-gray-700 leading-relaxed text-sm">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const EssayEditor: React.FC<EssayEditorProps> = ({
  initialContent = "",
  initialPrompt = "",
  initialTitle = "",
  initialComments = [],
  initialSummary = "",
  initialReport = null,
  analysisHistory = [],
  wordLimit,
  onSave,
  onAnalysisSave,
  onEssayOnlySave,
  onSaveTitle,
  onDeleteAnalysis,
  onRenameAnalysis,
  onUnsavedChanges,
  onClose,
  essayId,
  collegeName,
  overridePermission,
}) => {
  const { user, subscriptionData, subscriptionLoading, refreshSubscription } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [summary, setSummary] = useState(initialSummary);
  const [report, setReport] = useState<ScoreReport | null>(initialReport);

  // Debug: Log when report is loaded from database
  useEffect(() => {
    if (initialReport) {
      console.log("Report loaded from database:", initialReport);
    }
  }, [initialReport]);
  const [title, setTitle] = useState(initialTitle);
  const [selectedAnalysisIndex, setSelectedAnalysisIndex] = useState<
    number | null
  >(null);
  const [editingName, setEditingName] = useState<number | null>(null);
  const [editedName, setEditedName] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<number | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFeatureUpgradeModal, setShowFeatureUpgradeModal] = useState(false);
  const [upgradeFeatureType, setUpgradeFeatureType] = useState<
    "saved_analyses" | "ideation"
  >("saved_analyses");
  const [previousTabBeforeIdeation, setPreviousTabBeforeIdeation] = useState<
    "editor" | "ideas"
  >("editor");

  // Helper function to close feature upgrade modal and revert tab if needed
  const closeFeatureUpgradeModal = () => {
    setShowFeatureUpgradeModal(false);
    // If we were showing ideation modal, revert back to previous tab
    if (upgradeFeatureType === "ideation") {
      setActiveMainTab(previousTabBeforeIdeation);
    }
  };

  const [selectedModel, setSelectedModel] = useState<string>("gpt-5-mini");
  const [wordCount, setWordCount] = useState(0);
  const [showGPT5Notice, setShowGPT5Notice] = useState(false);
  const { captureEvent } = usePostHog();
  const [activeTab, setActiveTab] = useState("comments");
  const [activeMainTab, setActiveMainTab] = useState<"editor" | "ideas">(
    "editor"
  );

  // Sidebar collapsed state - collapsed by default if no comments or report
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    !(initialComments.length > 0 || initialReport !== null)
  );

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [shareEmailInput, setShareEmailInput] = useState("");
  const [sharePermission, setSharePermission] = useState<
    "read" | "comment" | "edit"
  >("comment");
  const [existingShares, setExistingShares] = useState<EssayShare[]>([]);
  const [updatedPermissions, setUpdatedPermissions] = useState<
    Record<string, "read" | "comment" | "edit">
  >({});
  // Public sharing state
  const [publicShareLink, setPublicShareLink] =
    useState<PublicShareLink | null>(null);
  const [isLoadingPublicLink, setIsLoadingPublicLink] = useState(false);
  const [isTogglingShare, setIsTogglingShare] = useState(false);

  // Live presence state
  const [activeUsers, setActiveUsers] = useState<
    Array<{
      user_id: string;
      name?: string;
      email?: string;
      profile_image_url?: string;
      is_focused?: boolean;
    }>
  >([]);
  const [anonymousViewers, setAnonymousViewers] = useState<
    Array<{
      id: string;
      animal_name: string;
      session_start: string;
      last_activity: string;
    }>
  >([]);

  // Tooltip state for presence icons
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  // Permission state
  const [userPermission, setUserPermission] = useState<
    "read" | "comment" | "edit" | "owner" | null
  >("owner");
  const [permissionLoading, setPermissionLoading] = useState(false);

  // Saved analyses modal state
  const [showSavedAnalysesModal, setShowSavedAnalysesModal] = useState(false);

  // Resizable sections state
  const [promptHeight, setPromptHeight] = useState(120); // in pixels
  const [editorHeight, setEditorHeight] = useState(700); // in pixels
  const [sidebarWidth, setSidebarWidth] = useState(35); // in percentage
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const [isResizingEditor, setIsResizingEditor] = useState(false);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startHeight, setStartHeight] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Chat state - persists across tab switches
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Suggestions state - persists across tab switches
  const [suggestions, setSuggestions] = useState<
    { angle: string; how_to_use: string; example_sentence?: string }[]
  >([]);

  // Tips state - for prompt-specific tips
  const [promptTips, setPromptTips] = useState<string[]>([]);
  const [promptTipsLoading, setPromptTipsLoading] = useState(false);
  const [promptTipsError, setPromptTipsError] = useState<string>("");

  // Handle resize for all sections
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizingPrompt) {
        const deltaY = e.clientY - startY;
        const newHeight = Math.max(80, Math.min(startHeight + deltaY, 500)); // Min 80px, max 500px
        setPromptHeight(newHeight);
      }
      if (isResizingEditor) {
        const deltaY = e.clientY - startY;
        const newHeight = Math.max(300, Math.min(startHeight + deltaY, 1200)); // Min 300px, max 1200px
        setEditorHeight(newHeight);
      }
      if (isResizingSidebar) {
        const deltaX = startX - e.clientX; // Reverse direction for sidebar
        const containerWidth = window.innerWidth;
        const newWidthPx = startWidth + deltaX;
        const newWidthPercent = Math.max(
          20,
          Math.min((newWidthPx / containerWidth) * 100, 50)
        ); // Min 20%, max 50%
        setSidebarWidth(newWidthPercent);
      }
    };

    const handleMouseUp = () => {
      setIsResizingPrompt(false);
      setIsResizingEditor(false);
      setIsResizingSidebar(false);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (isResizingPrompt || isResizingEditor || isResizingSidebar) {
      if (isResizingSidebar) {
        document.body.style.cursor = "col-resize";
      } else {
        document.body.style.cursor = "row-resize";
      }
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isResizingPrompt,
    isResizingEditor,
    isResizingSidebar,
    startY,
    startX,
    startHeight,
    startWidth,
  ]);

  // Check user permissions for this essay
  useEffect(() => {
    const checkPermissions = async () => {
      // If override permission is provided, use it directly
      if (overridePermission) {
        setUserPermission(
          overridePermission as "read" | "comment" | "edit" | "owner"
        );
        setPermissionLoading(false);
        return;
      }

      if (!user?.id) {
        setUserPermission("owner");
        setPermissionLoading(false);
        return;
      }

      // Use provided essayId or extract from URL
      const pathSegment = window.location.pathname.split("/").pop();
      const targetEssayId =
        essayId ||
        (pathSegment && isValidUUID(pathSegment) ? pathSegment : null);
      if (!targetEssayId) {
        setUserPermission("owner");
        setPermissionLoading(false);
        return;
      }

      try {
        setPermissionLoading(true);
        const permission = await DatabaseService.getUserEssayPermission(
          targetEssayId,
          user.id
        );
        setUserPermission(permission || "owner");
      } catch (error) {
        console.error("Error checking essay permissions:", error);
        setUserPermission("owner");
      } finally {
        setPermissionLoading(false);
      }
    };

    checkPermissions();
  }, [user?.id, location.pathname, essayId, overridePermission]);

  // Use refs to store current values to avoid callback recreation
  const promptRef = useRef(prompt);
  const titleRef = useRef(title);
  const onEssayOnlySaveRef = useRef(onEssayOnlySave);

  // Update refs when values change
  useEffect(() => {
    promptRef.current = prompt;
  }, [prompt]);

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  useEffect(() => {
    onEssayOnlySaveRef.current = onEssayOnlySave;
  }, [onEssayOnlySave]);

  // Auto-adjust prompt height based on content
  useEffect(() => {
    if (promptTextareaRef.current && prompt) {
      // Temporarily remove inline height to measure natural height
      const originalHeight = promptTextareaRef.current.style.height;
      promptTextareaRef.current.style.height = "auto";

      // Get the scroll height (natural content height)
      const scrollHeight = promptTextareaRef.current.scrollHeight;

      // Restore original height
      promptTextareaRef.current.style.height = originalHeight;

      // Calculate new container height with padding (48px for header, 24px for content padding, 20px extra breathing room)
      const headerHeight = 48;
      const contentPadding = 24;
      const extraSpace = 20; // Extra space at bottom for breathing room
      const newHeight = Math.max(
        120,
        Math.min(scrollHeight + headerHeight + contentPadding + extraSpace, 500)
      );

      setPromptHeight(newHeight);
    }
  }, [prompt, initialPrompt]); // Re-run when prompt changes

  // Helper function to format relative time
  const formatTimeAgo = (date: Date | string): string => {
    try {
      const now = new Date();
      const dateObj = typeof date === "string" ? new Date(date) : date;

      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return "unknown time";
      }

      const diffInMs = now.getTime() - dateObj.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInMinutes < 1) {
        return "just now";
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
      } else {
        return dateObj.toLocaleDateString();
      }
    } catch (error) {
      console.error("Error formatting time:", error);
      return "unknown time";
    }
  };

  // Helper function to get display name from author email
  const getDisplayName = (authorEmail: string): string => {
    // For now, extract name from email prefix
    // In the future, this could lookup actual user names from the database
    if (authorEmail && authorEmail.includes("@")) {
      const nameFromEmail = authorEmail.split("@")[0];
      // Convert recruiterrank -> Recruiter Rank (basic formatting)
      return nameFromEmail
        .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to spaces
        .replace(/[._-]/g, " ") // replace dots, underscores, dashes with spaces
        .replace(/\b\w/g, (l) => l.toUpperCase()); // capitalize first letter of each word
    }
    return authorEmail || "Anonymous";
  };

  // Tooltip helper functions
  const showTooltip = (event: React.MouseEvent, text: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 320; // Max width from the tooltip styles
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate horizontal position
    let x = rect.left + rect.width / 2;

    // Adjust if tooltip would go off the right edge (extra margin to avoid scrollbar)
    if (x + tooltipWidth / 2 > viewportWidth - 32) {
      x = viewportWidth - tooltipWidth / 2 - 32;
    }

    // Adjust if tooltip would go off the left edge
    if (x - tooltipWidth / 2 < 16) {
      x = tooltipWidth / 2 + 16;
    }

    // Calculate vertical position
    let y = rect.bottom + 8;

    // If tooltip would go off the bottom, show it above the element
    if (y + 60 > viewportHeight) {
      // Approximate tooltip height
      y = rect.top - 8;
    }

    setTooltip({
      text,
      x,
      y,
    });
  };

  const hideTooltip = () => {
    setTooltip(null);
  };

  // Auto-save when comments change (for reviewers)
  useEffect(() => {
    if (userPermission === "comment" && comments.length > 0 && onSave) {
      const timeoutId = setTimeout(async () => {
        try {
          await onSave(
            "", // Don't save content for reviewers
            comments,
            summary,
            prompt,
            title,
            report
          );
        } catch (error) {
          console.error("Error auto-saving comments:", error);
        }
      }, 1000); // Save 1 second after comment changes

      return () => clearTimeout(timeoutId);
    }
  }, [comments, userPermission, onSave, summary, prompt, title, report]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              class: "tiptap-paragraph",
            },
          },
        }),
        Placeholder.configure({
          placeholder:
            userPermission === "read"
              ? "This essay is shared with you in read-only mode."
              : userPermission === "comment"
              ? "This essay is shared with you in comment-only mode. You can add comments by selecting text."
              : "Start writing your essay here...",
        }),
        CommentHighlight.configure({
          HTMLAttributes: {
            class: "comment-highlight",
          },
        }),
      ],
      content: initialContent,
      editable: userPermission === "edit" || userPermission === "owner",
      editorProps: {
        attributes: {
          class: "prose prose-sm max-w-none focus:outline-none",
        },
        handlePaste: (view, event) => {
          // Prevent paste for users without edit permissions
          if (userPermission !== "edit" && userPermission !== "owner") {
            return true; // Prevent default paste behavior
          }

          const clipboardData = event.clipboardData;
          if (!clipboardData) return false;

          const text = clipboardData.getData("text/plain");
          if (text) {
            // Split text by double newlines to preserve paragraph structure
            // but avoid creating extra empty paragraphs for single newlines
            const paragraphs = text.split(/\r?\n\r?\n/);
            const { tr } = view.state;
            const { from, to } = view.state.selection;

            // Delete selected content first
            let transaction = tr.delete(from, to);

            // Insert each paragraph
            paragraphs.forEach((paragraph, index) => {
              // Replace single newlines within paragraphs with spaces
              const cleanParagraph = paragraph.replace(/\r?\n/g, " ").trim();

              if (cleanParagraph) {
                if (index === 0) {
                  // Insert first paragraph at current position
                  transaction = transaction.insertText(cleanParagraph);
                } else {
                  // Create new paragraph for subsequent paragraphs
                  transaction = transaction.split(transaction.selection.from);
                  transaction = transaction.insertText(cleanParagraph);
                }
              }
            });

            view.dispatch(transaction);
            return true; // Prevent default paste behavior
          }
          return false;
        },
      },
      onCreate: ({ editor }) => {
        setWordCount(
          editor.getText().trim().split(/\s+/).filter(Boolean).length
        );
      },
      onUpdate: ({ editor }) => {
        if (userPermission === "edit" || userPermission === "owner") {
          handleContentChange();
        }
        setWordCount(
          editor.getText().trim().split(/\s+/).filter(Boolean).length
        );
      },
    },
    [userPermission]
  );

  // Auto-save content changes
  const handleContentChange = useCallback(() => {
    // Don't autosave during initial loading
    if (isInitializing.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      if (!editor) return;
      if (!onEssayOnlySaveRef.current) {
        console.error("onEssayOnlySave is not available");
        return;
      }
      try {
        setIsSaving(true);
        const contentToSave = editor.getHTML();
        console.log("Auto-saving content:", contentToSave);
        await onEssayOnlySaveRef.current(
          contentToSave,
          promptRef.current,
          titleRef.current
        );
        console.log("Auto-save completed successfully");
        setLastSaved(new Date());
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  }, [editor]);

  // Auto-save prompt changes
  const handlePromptChange = (newPrompt: string) => {
    setPrompt(newPrompt);

    // Don't autosave during initial loading
    if (isInitializing.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      if (!editor) return;
      if (!onEssayOnlySaveRef.current) {
        console.error("onEssayOnlySave is not available for prompt save");
        return;
      }
      try {
        setIsSaving(true);
        const contentToSave = editor.getHTML();
        console.log("Auto-saving content (prompt change):", contentToSave);
        await onEssayOnlySaveRef.current(
          contentToSave,
          newPrompt,
          titleRef.current
        );
        console.log("Auto-save (prompt) completed successfully");
        setLastSaved(new Date());
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    captureEvent("prompt_updated", {
      promptLength: newPrompt.length,
    });
  };

  // Subscription data is now handled by AuthContext - no need for separate useEffect

  // Update editor content when initialContent changes (e.g., after loading from database)
  useEffect(() => {
    if (editor && initialContent && initialContent !== editor.getHTML()) {
      console.log(
        "Setting editor content from initialContent:",
        initialContent
      );
      // Use a ref to prevent triggering autosave during content sync
      const currentHandler = handleContentChange;
      editor.commands.setContent(initialContent, false);
      // Update word count after setting content
      setWordCount(editor.getText().trim().split(/\s+/).filter(Boolean).length);
    }
  }, [editor, initialContent]);

  // Update prompt when initialPrompt prop changes (only if current prompt is empty)
  useEffect(() => {
    if (initialPrompt && !prompt) {
      console.log("Setting prompt from initialPrompt:", initialPrompt);
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  // Fetch prompt-specific tips for subscribed users
  useEffect(() => {
    let cancelled = false;

    async function fetchPromptTips() {
      // Only fetch if user is subscribed (not on free plan)
      if (subscriptionData?.plan === "free") {
        return;
      }

      if (!prompt || prompt.trim().length < 8) {
        setPromptTips([]);
        return;
      }

      // Use cache if available to avoid API calls
      const cached = promptTipsCache.get(prompt);
      if (cached) {
        setPromptTips(cached);
        return;
      }

      // Avoid duplicate calls for the same prompt if a request is in-flight
      if (inFlightTips.has(prompt)) {
        return;
      }

      try {
        inFlightTips.add(prompt);
        setPromptTipsLoading(true);
        setPromptTipsError("");
        const res = await axios.post(API_ENDPOINTS.ideate, {
          prompt,
          ideas: [],
          model: "gpt-5-mini",
        });
        const nextTips = Array.isArray(res.data?.tips) ? res.data.tips : [];
        // Cache tips for future use
        promptTipsCache.set(prompt, nextTips);
        if (!cancelled) {
          setPromptTips(nextTips);
        }
      } catch (e) {
        if (!cancelled) {
          setPromptTipsError("Failed to load prompt tips");
        }
      } finally {
        inFlightTips.delete(prompt);
        if (!cancelled) setPromptTipsLoading(false);
      }
    }

    // Debounce fetch slightly
    const id = setTimeout(fetchPromptTips, 400);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [prompt, subscriptionData?.plan]);

  // Update title when initialTitle prop changes (only if current title is empty)
  useEffect(() => {
    if (initialTitle && !title) {
      console.log("Setting title from initialTitle:", initialTitle);
      setTitle(initialTitle);
    }
  }, [initialTitle]);

  // Update comments when initialComments prop changes
  useEffect(() => {
    if (
      initialComments &&
      JSON.stringify(initialComments) !== JSON.stringify(comments)
    ) {
      console.log("Setting comments from initialComments:", initialComments);
      setComments(initialComments);
    }
  }, [initialComments]);

  // Update summary when initialSummary prop changes
  useEffect(() => {
    if (initialSummary !== summary) {
      console.log("Setting summary from initialSummary:", initialSummary);
      setSummary(initialSummary);
    }
  }, [initialSummary]);

  // Update report when initialReport prop changes
  useEffect(() => {
    if (JSON.stringify(initialReport) !== JSON.stringify(report)) {
      console.log("Setting report from initialReport:", initialReport);
      setReport(initialReport);
    }
  }, [initialReport]);

  // Auto-expand sidebar when comments or report are added
  useEffect(() => {
    const hasContent = comments.length > 0 || report !== null;
    if (hasContent) {
      setSidebarCollapsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments.length, report]);

  // Update sidebar state when initial data loads
  useEffect(() => {
    const hasInitialContent =
      initialComments.length > 0 || initialReport !== null;
    setSidebarCollapsed(!hasInitialContent);
  }, [initialComments.length, initialReport]);

  const sidebarRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isInitializing = useRef(true);

  // Reset initialization flag when essay changes
  useEffect(() => {
    isInitializing.current = true;
  }, [essayId]);

  // Set initialization to false after all initial props are loaded
  useEffect(() => {
    if (editor && (initialContent || initialPrompt || initialTitle)) {
      // Small delay to ensure all initialization is complete
      const timer = setTimeout(() => {
        isInitializing.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editor, initialContent, initialPrompt, initialTitle]);

  useEffect(() => {
    if (!editor) return;

    // Add a small delay to ensure editor content is fully loaded
    const timer = setTimeout(() => {
      // Process both AI comments and finalized user comments for highlighting
      const highlightableComments = comments.filter((c) => {
        if (c.type === "ai") {
          return true; // Include all AI comments
        } else {
          // Include finalized user comments that have text
          return c.finalized && c.text && c.text.trim() !== "";
        }
      });

      const positions = [] as {
        from: number;
        to: number;
        comment: string;
        id: string;
        cssClass?: string;
      }[];
      const editorText = editor.getText();
      const doc = editor.state.doc;

      highlightableComments.forEach((comment) => {
        // Get the text to highlight based on comment type
        const textToHighlight =
          comment.type === "ai" ? comment.phrase : comment.selectedText;
        const commentText =
          comment.type === "ai" ? comment.comment : comment.text;

        // Skip if comment doesn't have the required text field
        if (!textToHighlight || !editorText || !commentText) {
          return;
        }

        let start: number, end: number;

        if (comment.type === "user" && comment.position !== undefined) {
          // For user comments, use the stored position data directly
          start = comment.position;
          end =
            comment.endPosition || comment.position + textToHighlight.length;
        } else {
          // For AI comments, fall back to text search (since they don't have position data)
          const phraseIndex = editorText.indexOf(textToHighlight);

          if (phraseIndex === -1) {
            // Try case-insensitive search as backup
            const phraseIndexInsensitive = editorText
              .toLowerCase()
              .indexOf(textToHighlight.toLowerCase());
            if (phraseIndexInsensitive === -1) {
              return; // Skip this comment if text not found
            }
            start = phraseIndexInsensitive + 1; // +1 to account for ProseMirror positioning
            end = start + textToHighlight.length;
          } else {
            start = phraseIndex + 1; // +1 to account for ProseMirror positioning
            end = start + textToHighlight.length;
          }
        }

        positions.push({
          from: start,
          to: end,
          comment: commentText,
          id: comment.id,
          cssClass:
            comment.type === "ai"
              ? "comment-highlight ai-comment"
              : "comment-highlight user-comment",
        });
      });

      // Sort positions by start index
      positions.sort((a, b) => a.from - b.from);

      // Remove overlapping highlights (keep the first one)
      const nonOverlapping: typeof positions = [];
      positions.forEach((pos) => {
        const hasOverlap = nonOverlapping.some(
          (existing) => pos.from < existing.to && pos.to > existing.from
        );
        if (!hasOverlap) {
          nonOverlapping.push(pos);
        }
      });
      editor.commands.setCommentHighlights(nonOverlapping);
    }, 150); // Small delay to ensure content is set

    return () => clearTimeout(timer);
  }, [comments, editor, initialContent]);

  useEffect(() => {
    if (!editor) return;

    // Attach click and hover handlers
    const dom = editor.view.dom;
    let rafId: number | null = null;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const id = target.getAttribute("data-comment-id");
      if (id && sidebarRefs.current[id]) {
        // Scroll to center the comment in the viewport
        sidebarRefs.current[id]!.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Add highlight effect
        const commentElement = sidebarRefs.current[id]!;
        commentElement.classList.add("comment-highlight-flash");

        // Remove highlight after animation
        setTimeout(() => {
          commentElement.classList.remove("comment-highlight-flash");
        }, 2000);
      }
    };

    const calculateTooltipPosition = (clientX: number, clientY: number) => {
      const tooltipWidth = 320; // max-width from CSS
      const tooltipHeight = 80; // estimated height
      const padding = 20;

      let x = clientX + 15;
      let y = clientY - 15;

      // Prevent going off the right edge
      if (x + tooltipWidth + padding > window.innerWidth) {
        x = clientX - tooltipWidth - 15;
      }

      // Prevent going off the left edge
      if (x < padding) {
        x = padding;
      }

      // Prevent going off the top edge
      if (y < padding) {
        y = clientY + 25;
      }

      // Prevent going off the bottom edge
      if (y + tooltipHeight + padding > window.innerHeight) {
        y = clientY - tooltipHeight - 25;
      }

      return { x, y };
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("comment-highlight")) {
        let commentText = target.getAttribute("data-comment");

        // If no data-comment attribute (user comments), try to find by comment ID
        if (!commentText || !commentText.trim()) {
          const commentId = target.getAttribute("data-comment-id");
          if (commentId) {
            const userComment = comments.find(
              (c) => c.id === commentId && c.type === "user"
            ) as UserComment;
            if (userComment && userComment.text && userComment.text.trim()) {
              commentText = userComment.text;
            }
          }
        }

        if (commentText && commentText.trim()) {
          const position = calculateTooltipPosition(e.clientX, e.clientY);
          setTooltip({
            x: position.x,
            y: position.y,
            text: commentText as string, // We know it's a string because of the check above
          });
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if we're over a comment highlight
      if (target.classList.contains("comment-highlight")) {
        let commentText = target.getAttribute("data-comment");

        // If no data-comment attribute (user comments), try to find by comment ID
        if (!commentText || !commentText.trim()) {
          const commentId = target.getAttribute("data-comment-id");
          if (commentId) {
            const userComment = comments.find(
              (c) => c.id === commentId && c.type === "user"
            ) as UserComment;
            if (userComment && userComment.text && userComment.text.trim()) {
              commentText = userComment.text;
            }
          }
        }

        if (commentText && commentText.trim()) {
          // Cancel previous animation frame to avoid stacking
          if (rafId) {
            cancelAnimationFrame(rafId);
          }

          // Always update both position and content to handle moving between different highlights
          rafId = requestAnimationFrame(() => {
            const position = calculateTooltipPosition(e.clientX, e.clientY);
            setTooltip({
              x: position.x,
              y: position.y,
              text: commentText as string, // We know it's a string because of the check above
            });
            rafId = null;
          });
        }
      } else if (tooltip) {
        // Hide tooltip if we're not over a highlight anymore
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        setTooltip(null);
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement;

      // Only hide tooltip if we're actually leaving the highlight (not moving to a child)
      if (
        target.classList.contains("comment-highlight") &&
        (!relatedTarget || !target.contains(relatedTarget))
      ) {
        // Cancel any pending animation frame
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        setTooltip(null);
      }
    };

    dom.addEventListener("click", handleClick);
    dom.addEventListener("mouseover", handleMouseOver, false);
    dom.addEventListener("mousemove", handleMouseMove, false);
    dom.addEventListener("mouseout", handleMouseOut, false);

    return () => {
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      dom.removeEventListener("click", handleClick);
      dom.removeEventListener("mouseover", handleMouseOver, false);
      dom.removeEventListener("mousemove", handleMouseMove, false);
      dom.removeEventListener("mouseout", handleMouseOut, false);
    };
  }, [editor, tooltip]);

  const addUserComment = () => {
    const selection = editor?.state.selection;
    if (selection) {
      const selectedText = editor?.state.doc.textBetween(
        selection.from,
        selection.to,
        " "
      );
      const hasSelection = selection.from !== selection.to;

      const newComment: UserComment = {
        id: Date.now().toString(),
        text: "",
        position: selection.from,
        endPosition: hasSelection ? selection.to : undefined,
        selectedText: hasSelection ? selectedText : undefined,
        createdAt: new Date(),
        type: "user",
        author: user?.email || "Anonymous",
        finalized: false, // Start as non-finalized for editing
      };

      // Note: We don't apply UserCommentMark here since we rely on the CommentHighlight decoration system
      // The highlighting will be applied by the useEffect that processes all comments

      setComments((prevComments) => [newComment, ...prevComments]);
    }
  };

  // Chat handlers
  const handleOpenChat = () => {
    setChatOpen(true);
    if (chatMessages.length === 0) {
      setChatMessages([
        {
          role: "assistant",
          content:
            "Hi! I'm here to help you explore and expand on your brainstorming ideas. What would you like to discuss about your suggestions?",
        },
      ]);
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
  };

  // Helper functions for email management
  const addEmail = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    if (
      trimmedEmail &&
      trimmedEmail.includes("@") &&
      !shareEmails.includes(trimmedEmail)
    ) {
      setShareEmails([...shareEmails, trimmedEmail]);
      setShareEmailInput("");
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setShareEmails(shareEmails.filter((email) => email !== emailToRemove));
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail(shareEmailInput);
    }
  };

  const loadExistingShares = useCallback(async () => {
    if (!user?.id) return;

    const pathSegment = window.location.pathname.split("/").pop();
    const targetEssayId =
      essayId || (pathSegment && isValidUUID(pathSegment) ? pathSegment : null);
    if (!targetEssayId) return;

    try {
      const shares = await DatabaseService.getEssayShares(targetEssayId);
      setExistingShares(shares);
    } catch (error) {
      console.error("Failed to load existing shares:", error);
    }
  }, [user?.id, essayId]);

  // Public sharing functions
  const loadPublicShareLink = useCallback(async () => {
    if (!user?.id) return;
    const pathSegment = window.location.pathname.split("/").pop();
    const targetEssayId =
      essayId || (pathSegment && isValidUUID(pathSegment) ? pathSegment : null);
    if (!targetEssayId) return;

    setIsLoadingPublicLink(true);
    try {
      const link = await DatabaseService.getEssayPublicShareLink(
        targetEssayId,
        user.id
      );
      setPublicShareLink(link);
    } catch (error) {
      console.error("Failed to load public share link:", error);
    } finally {
      setIsLoadingPublicLink(false);
    }
  }, [user?.id, essayId]);

  // Load existing shares when modal opens
  // Only reload public share link if we don't have it yet
  useEffect(() => {
    if (showShareModal) {
      loadExistingShares();
      // Only load public share link if we don't have it yet (avoid overwriting toggled state)
      if (!publicShareLink) {
        loadPublicShareLink();
      }
    }
  }, [
    showShareModal,
    loadExistingShares,
    loadPublicShareLink,
    publicShareLink,
  ]);

  // Load public share link on mount to enable anonymous user tracking
  useEffect(() => {
    if (user?.id && (essayId || window.location.pathname.includes("/essay/"))) {
      loadPublicShareLink();
    }
  }, [user?.id, essayId, loadPublicShareLink]);

  // Note: Read-only user redirect logic has been moved to EssayRouter component

  // Cleanup auto-save timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleSendMessage = async (suggestionsContext: any[] = []) => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");

    // Add user message
    setChatMessages((prev) => [
      ...prev,
      { role: "user", content: userMessage },
    ]);
    setChatLoading(true);

    try {
      const contextSuggestions =
        suggestionsContext.length > 0 ? suggestionsContext : suggestions;

      const response = await axios.post(API_ENDPOINTS.chat, {
        messages: [...chatMessages, { role: "user", content: userMessage }],
        prompt: prompt,
        suggestions: contextSuggestions,
        model: "gpt-5-mini",
        user_id: user?.id,
      });

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.message.content },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleAnalyze = async () => {
    console.log(
      "ANALYZE BUTTON CLICKED - This should ONLY happen on button click"
    );
    if (!editor || !prompt.trim() || !user?.id) {
      toast.error("Please provide both a prompt and essay text");
      return;
    }

    const wordCount = editor.getText().trim().split(/\s+/).length;
    if (wordCount < 50) {
      toast.error("Please write at least 50 words before analyzing.");
      return;
    }

    // Check if user has reached the limit (5 analyses for free users)
    if (
      subscriptionData?.plan === "free" &&
      subscriptionData?.usageCount >= 5
    ) {
      setShowUpgradeModal(true);
      return;
    }

    captureEvent("essay_analysis_started", {
      essayLength: editor.getText().length,
      hasPrompt: Boolean(prompt),
    });

    const startTime = Date.now();

    try {
      setIsAnalyzing(true);

      // Show notice for GPT-5 models that take longer
      if (selectedModel === "gpt-5") {
        setShowGPT5Notice(true);
      }

      // Increment usage count before analysis
      const incrementSuccess = await DatabaseService.incrementUserAnalysisCount(
        user.id
      );

      if (!incrementSuccess) {
        throw new Error("Failed to update usage count");
      }

      const response = await axios.post(API_ENDPOINTS.analyze, {
        essay: editor.getText(),
        prompt,
        user_id: user.id,
        model: selectedModel,
      });

      let aiComments: AIComment[] = [];

      if (typeof response.data.comments === "string") {
        try {
          const parsedComments = JSON.parse(response.data.comments);
          aiComments = parsedComments.map((comment: any) => ({
            ...comment,
            id: Date.now().toString() + Math.random(),
            type: "ai",
          }));
        } catch (e) {
          console.error("Failed to parse comments JSON:", e);
          toast.error("Failed to parse analysis results");
          return;
        }
      } else {
        aiComments = response.data.comments.map((comment: any) => ({
          ...comment,
          id: Date.now().toString() + Math.random(),
          type: "ai",
        }));
      }

      const newComments = [
        ...comments.filter((c) => c.type === "user"),
        ...aiComments,
      ];
      const newSummary = response.data.summary;
      const newReport = response.data.report;

      // Keep user comments and add AI comments
      setComments(newComments);
      setSummary(newSummary);
      setReport(newReport);

      // Save the analysis to history
      if (onAnalysisSave) {
        await onAnalysisSave(
          editor.getHTML(),
          newComments,
          newSummary,
          prompt,
          title,
          newReport
        );
      }

      // Auto-switch to report tab if we got a report
      if (response.data.report) {
        setActiveTab("report");
      }

      // Update usage count
      // setUsageCount((prev) => prev + 1); // Removed as per edit hint
      refreshSubscription(); // Refresh subscription data after analysis

      // Hide GPT-5 notice on successful completion
      setShowGPT5Notice(false);

      captureEvent("essay_analysis_completed", {
        essayLength: editor.getText().length,
        analysisTime: Date.now() - startTime,
        success: true,
      });
    } catch (err: unknown) {
      console.error("Error analyzing essay:", err);
      toast.error(
        "An error occurred while analyzing your essay. Please try again."
      );
      // Hide GPT-5 notice on error
      setShowGPT5Notice(false);
      captureEvent("essay_analysis_failed", {
        essayLength: editor.getText()?.length || 0,
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveEssaySettings = async (
    essayType: "common-app" | "supplemental",
    newPrompt: string
  ) => {
    if (!essayId) return;

    try {
      // Update the prompt in the database
      await DatabaseService.updateEssay(essayId, {
        prompt: newPrompt,
      });

      // Update local state
      setPrompt(newPrompt);

      console.log("Essay settings updated successfully");
    } catch (error) {
      console.error("Error updating essay settings:", error);
    }
  };

  const handleUpgradeClick = async () => {
    if (!user) return;

    try {
      const response = await fetch(API_ENDPOINTS.createCheckoutSession, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          line_items: [
            {
              price: process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to create checkout session"
        );
      }

      const { sessionId } = await response.json();

      const stripe = await stripePromise;
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          throw error;
        }
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to initiate upgrade. Please try again.");
    }
  };

  const getCommentedText = (comment: AIComment) => {
    return comment.phrase || "";
  };

  // Load selected analysis
  const loadAnalysis = (index: number) => {
    const analysis = analysisHistory[index];
    if (analysis && editor) {
      // First set the content
      editor.commands.setContent(analysis.content);

      // Then set the prompt
      setPrompt(analysis.prompt);

      // Convert comment dates back to Date objects
      const parsedComments = analysis.comments.map((comment) => {
        if (comment.type === "user" && typeof comment.createdAt === "string") {
          return {
            ...comment,
            createdAt: new Date(comment.createdAt),
          };
        }
        return comment;
      }) as Comment[];

      // Set comments after content is loaded to ensure proper highlighting
      setComments(parsedComments);
      setSummary(analysis.summary);
      setReport(analysis.report || null);
      setSelectedAnalysisIndex(index);
    }
  };

  // Add helper for default name
  const getDefaultAnalysisName = (index: number) => `Analysis #${index + 1}`;

  const handleDeleteClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the analysis selection
    setAnalysisToDelete(index);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (analysisToDelete !== null && onDeleteAnalysis) {
      onDeleteAnalysis(analysisToDelete);
      setDeleteModalOpen(false);
      setAnalysisToDelete(null);
    }
  };

  // Model options for the dropdown
  const modelOptions: DropdownOption[] = [
    {
      value: "gpt-5-mini",
      label: "GPT-5 Mini",
      description: "Fast and efficient (~20s)",
    },
    {
      value: "gpt-5",
      label: "GPT-5",
      description: "Most accurate (~1-2 min)",
    },
    {
      value: "gpt-4o",
      label: "GPT-4o",
      description: "Fast and efficient (~20s)",
    },
    {
      value: "gemini-2.5-flash",
      label: "Gemini 2.5 Flash",
      description: "Quick responses (~15s)",
    },
    {
      value: "gemini-2.5-pro",
      label: "Gemini 2.5 Pro",
      description: "Advanced reasoning (~45s)",
    },
  ];

  const handlePermissionChange = (
    shareId: string,
    permission: "read" | "comment" | "edit"
  ) => {
    setUpdatedPermissions((prev) => ({ ...prev, [shareId]: permission }));
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await DatabaseService.deleteEssayShare(shareId);
      toast.success("Share removed successfully!");
      loadExistingShares();
    } catch (error) {
      toast.error("Failed to remove share. Please try again.");
    }
  };

  const handleTogglePublicSharing = async () => {
    if (!user?.id || isTogglingShare) return; // Prevent spam clicking

    const pathSegment = window.location.pathname.split("/").pop();
    const targetEssayId =
      essayId || (pathSegment && isValidUUID(pathSegment) ? pathSegment : null);
    if (!targetEssayId) {
      toast.error("Essay ID not found");
      return;
    }

    setIsTogglingShare(true);

    // Store the original state for rollback if needed
    const originalLink = publicShareLink;

    // Optimistic update - immediately update the UI
    if (publicShareLink) {
      const optimisticUpdate = {
        ...publicShareLink,
        is_active: !publicShareLink.is_active,
      };
      setPublicShareLink(optimisticUpdate);
    }

    try {
      const updatedLink = await DatabaseService.toggleEssayPublicSharing(
        targetEssayId,
        user.id
      );
      if (updatedLink) {
        // Update with the actual server response - this is the source of truth
        setPublicShareLink(updatedLink);
        const action = updatedLink.is_active ? "enabled" : "disabled";
        toast.success(`Public sharing ${action} successfully!`);
      } else {
        // Rollback optimistic update on failure
        setPublicShareLink(originalLink);
        toast.error("Failed to toggle public sharing. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling public sharing:", error);
      // Rollback optimistic update on error
      setPublicShareLink(originalLink);

      // Provide more specific error messages
      if (error instanceof Error && error.message.includes("500")) {
        toast.error("Server error occurred. Please try again in a moment.");
      } else if (error instanceof Error && error.message.includes("404")) {
        toast.error(
          "Essay not found or you don't have permission to share it."
        );
      } else {
        toast.error("Failed to toggle public sharing. Please try again.");
      }
    } finally {
      setIsTogglingShare(false);
    }
  };

  const copyPublicLinkToClipboard = () => {
    if (!publicShareLink) return;

    const publicUrl = `${window.location.origin}/share/${publicShareLink.share_token}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast.success("Link copied to clipboard!");
    });
  };

  const handleShareOrSave = async () => {
    if (!user?.id) return;
    const pathSegment = window.location.pathname.split("/").pop();
    const targetEssayId =
      essayId || (pathSegment && isValidUUID(pathSegment) ? pathSegment : null);
    if (!targetEssayId) {
      toast.error("Essay ID not found");
      return;
    }

    let success = true;

    // Save updated permissions
    if (Object.keys(updatedPermissions).length > 0) {
      try {
        await Promise.all(
          Object.entries(updatedPermissions).map(([shareId, permission]) =>
            DatabaseService.updateEssayShare(shareId, {
              permission_level: permission,
            })
          )
        );
        setUpdatedPermissions({});
      } catch (error) {
        console.error("Failed to update permissions:", error);
        toast.error("Failed to save permission changes. Please try again.");
        success = false;
      }
    }

    // Share with new emails
    if (shareEmails.length > 0) {
      try {
        await DatabaseService.shareEssay(
          targetEssayId,
          user.id,
          shareEmails,
          sharePermission
        );
        setShareEmails([]);
        setShareEmailInput("");
      } catch (error) {
        console.error("Failed to share essay:", error);
        toast.error("Failed to share essay. Please try again.");
        success = false;
      }
    }

    if (success) {
      toast.success("Changes saved successfully!");
      loadExistingShares(); // Refresh the list of shares
    }
  };

  // Note: Read-only user handling has been moved to EssayRouter component

  return (
    <div>
      {showGPT5Notice && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-800 px-6 py-4 rounded-xl shadow-lg flex items-center gap-4 max-w-md">
          <div className="flex-shrink-0">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <div className="font-medium text-blue-900">
              GPT-5 Analysis in Progress
            </div>
            <div className="text-sm text-blue-700 mt-1">
              GPT-5 provides the most detailed analysis but takes approximately
              1 minute to complete.
            </div>
          </div>
          <button
            onClick={() => setShowGPT5Notice(false)}
            className="text-blue-400 hover:text-blue-600 transition-colors"
            title="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
      <div className="flex flex-col gap-2 p-2 w-full">
        <style>
          {`
            .comment-highlight {
              cursor: pointer;
              transition: all 0.2s ease;
              padding: 1px 2px;
              margin: 0;
              border-radius: 3px;
              display: inline;
              position: relative;
              box-decoration-break: clone;
              -webkit-box-decoration-break: clone;
            }

            /* AI Comments - Yellow/Amber */
            .comment-highlight.ai-comment {
              background-color: rgba(255, 212, 0, 0.25);
              border-bottom: 2px solid rgba(255, 170, 0, 0.6);
            }
            .comment-highlight.ai-comment:hover {
              background-color: rgba(255, 212, 0, 0.4);
              border-bottom-color: rgba(255, 170, 0, 0.9);
              box-shadow: 0 2px 4px rgba(255, 170, 0, 0.2);
            }

            /* User Comments - Purple (simplified, like AI comments) */
            .comment-highlight.user-comment {
              background-color: rgba(168, 85, 247, 0.25);
              border-bottom: 2px solid rgba(168, 85, 247, 0.6);
            }
            .comment-highlight.user-comment:hover {
              background-color: rgba(168, 85, 247, 0.4);
              border-bottom-color: rgba(168, 85, 247, 0.9);
              box-shadow: 0 2px 4px rgba(168, 85, 247, 0.2);
            }
            /* CSS-based tooltips disabled to prevent duplicates with sidebar tooltips */
            .ProseMirror {
              min-height: 400px;
              font-size: 16px;
              line-height: 1.6;
              padding: 1.5rem !important;
              outline: none !important;
            }
            .ProseMirror p {
              margin: 1em 0;
            }
            .ProseMirror p:first-child {
              margin-top: 0;
            }
            .prose {
              max-width: none;
              font-size: 16px;
            }
            .prose p {
              margin: 1em 0;
            }
            .editor-toolbar-button {
              padding: 0.5rem 1rem;
              font-size: 0.875rem;
              border-radius: 0.375rem;
              font-weight: 500;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }
            .editor-toolbar-button:focus {
              outline: 2px solid transparent;
              outline-offset: 2px;
              ring: 2px solid rgba(59, 130, 246, 0.5);
            }
            .editor-toolbar-button.active {
              background-color: #f3f4f6;
              color: #1f2937;
            }
            .comment-card {
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .comment-card:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }

            /* User comment card styling - Purple background like AI comments */
            .user-comment-card {
              background-color: #faf5ff;
              border: 1px solid rgba(168, 85, 247, 0.2);
            }
          `}
        </style>

        {/* Top-level tab navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex justify-between items-center">
            {/* Left side: Back button and Tabs */}
            <div className="flex items-center">
              {/* Back button */}
              {onClose && (
                <button
                  onClick={onClose}
                  className="flex items-center gap-1 mr-4 px-2 py-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md text-sm transition-colors"
                  title="Back to Essays"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                </button>
              )}
              <div className="flex space-x-2 items-center">
                <button
                  onClick={() => setActiveMainTab("editor")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeMainTab === "editor"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    Essay Editor
                  </div>
                </button>
                {userPermission !== "comment" && (
                  <button
                    onClick={() => {
                      // Check if user is on free plan and should be prompted to upgrade
                      if (
                        subscriptionData?.plan === "free" &&
                        userPermission === "owner"
                      ) {
                        // Store current tab before switching
                        setPreviousTabBeforeIdeation(activeMainTab);
                        // Switch to ideation tab first
                        setActiveMainTab("ideas");
                        // Then show the upgrade modal with a slight delay to allow tab switch
                        setTimeout(() => {
                          setUpgradeFeatureType("ideation");
                          setShowFeatureUpgradeModal(true);
                        }, 100);
                      } else {
                        setActiveMainTab("ideas");
                      }
                    }}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeMainTab === "ideas"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 11H6l3-9 3 9h-3z" />
                        <path d="M20 12h-6l6 9-6-9z" />
                      </svg>
                      Idea Creation
                    </div>
                  </button>
                )}
              </div>
              {/* Middle: Settings, Share Button and Save Status */}
              <div className="flex items-center gap-0 ml-0">
                {/* Settings Button - only show for owners */}
                {userPermission === "owner" && (
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className="group text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2 text-gray-500 group-hover:text-gray-900">
                      <svg
                        className="w-4 h-4 transition-transform group-hover:rotate-45"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Settings
                    </div>
                  </button>
                )}

                {/* Share Button - only show for owners */}
                {userPermission === "owner" && (
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="group text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2 text-gray-500 group-hover:text-gray-900">
                      <svg
                        className="w-4 h-4 transition-transform group-hover:scale-110"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                        />
                      </svg>
                      Share
                    </div>
                  </button>
                )}

                {isSaving ? (
                  <div
                    className="flex items-center gap-2 text-blue-600 ml-2"
                    title="Saving changes"
                  >
                    <svg
                      className="animate-spin w-4 h-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-xs font-medium">Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600 ml-2">
                    <svg
                      className="w-4 h-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-xs font-medium">
                      {lastSaved
                        ? `Saved ${lastSaved.toLocaleTimeString()}`
                        : "Saved"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right side: Permission indicator and Active Users */}
            <div className="flex items-center gap-4">
              {/* Permission indicator */}
              {userPermission && userPermission !== "owner" && (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    userPermission === "edit"
                      ? "bg-green-100 text-green-800"
                      : userPermission === "comment"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {userPermission === "edit"
                    ? "Edit"
                    : userPermission === "comment"
                    ? "Comment Only"
                    : "Read Only"}
                </span>
              )}

              {/* Live Active Users */}
              {(activeUsers.length > 0 || anonymousViewers.length > 0) && (
                <div className="flex -space-x-1">
                  {/* Authenticated Users */}
                  {activeUsers.slice(0, 2).map((activeUser) => {
                    const borderColor = activeUser.is_focused
                      ? "border-green-400"
                      : "border-yellow-400";
                    const dotColor = activeUser.is_focused
                      ? "bg-green-400"
                      : "bg-yellow-400";
                    const statusText = activeUser.is_focused
                      ? "actively viewing"
                      : "viewing (unfocused)";

                    return (
                      <div key={activeUser.user_id} className="relative">
                        <img
                          src={
                            activeUser.profile_image_url ||
                            `https://avatar.vercel.sh/${activeUser.email}.png`
                          }
                          alt={
                            activeUser.name || activeUser.email || "Active user"
                          }
                          onClick={
                            userPermission === "owner"
                              ? () => setShowShareModal(true)
                              : undefined
                          }
                          onMouseEnter={(e) =>
                            showTooltip(
                              e,
                              `${
                                activeUser.email || "Unknown email"
                              }\n${statusText}`
                            )
                          }
                          onMouseLeave={hideTooltip}
                          className={`w-8 h-8 rounded-full border-2 ${borderColor} shadow-sm hover:scale-110 transition-transform ${
                            userPermission === "owner"
                              ? "cursor-pointer"
                              : "cursor-default"
                          }`}
                        />
                        <div
                          className={`absolute -bottom-1 -right-1 w-3 h-3 ${dotColor} border-2 border-white rounded-full`}
                        ></div>
                      </div>
                    );
                  })}

                  {/* Anonymous Viewers */}
                  {anonymousViewers
                    .slice(0, Math.max(0, 3 - activeUsers.length))
                    .map((viewer) => (
                      <div key={viewer.id} className="relative">
                        <img
                          src={`https://avatar.vercel.sh/${viewer.animal_name}.png`}
                          alt={viewer.animal_name}
                          onMouseEnter={(e) =>
                            showTooltip(e, viewer.animal_name)
                          }
                          onMouseLeave={hideTooltip}
                          className="w-8 h-8 rounded-full border-2 border-blue-400 shadow-sm hover:scale-110 transition-transform"
                        />
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-400 border-2 border-white rounded-full"></div>
                      </div>
                    ))}

                  {/* Overflow counter */}
                  {activeUsers.length + anonymousViewers.length > 3 && (
                    <div
                      className="w-8 h-8 rounded-full border-2 border-gray-400 bg-gray-100 flex items-center justify-center shadow-sm relative group"
                      title={`+${
                        activeUsers.length + anonymousViewers.length - 3
                      } more viewers`}
                    >
                      <span className="text-xs font-medium text-gray-600">
                        +{activeUsers.length + anonymousViewers.length - 3}
                      </span>
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-400 border-2 border-white rounded-full"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
        </div>

        {activeMainTab === "editor" && (
          <div className="flex gap-0 h-full">
            <div
              className={`space-y-3 pr-4 ${
                isResizingSidebar ? "" : "transition-all duration-300"
              }`}
              style={{
                width: sidebarCollapsed
                  ? "calc(100% - 36px)"
                  : `${100 - sidebarWidth}%`,
              }}
            >
              {/* Replace the essay prompt div with styled panel */}
              <div
                className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-hidden relative flex flex-col"
                style={{ height: `${promptHeight}px` }}
              >
                <div className="p-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between min-h-12 flex-shrink-0">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2 flex-nowrap">
                    <BookOpen className="w-4 h-4" />
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                      }}
                      onBlur={() => onSaveTitle?.(title)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          onSaveTitle?.(title);
                          e.currentTarget.blur();
                        }
                      }}
                      placeholder="Enter essay title"
                      disabled={
                        userPermission !== "edit" && userPermission !== "owner"
                      }
                      className="flex-1 px-2 py-1 text-base font-semibold text-gray-900 bg-transparent border border-transparent rounded focus:outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 overflow-hidden text-ellipsis whitespace-nowrap"
                    />
                  </h3>

                  {/* Right side container with analyses count and clock icon */}
                  <div className="flex items-center gap-3">
                    {/* Analyses count for free users */}
                    {subscriptionLoading ? (
                      <div className="text-xs text-gray-500">
                        Loading subscription...
                      </div>
                    ) : subscriptionData?.plan === "free" &&
                      userPermission === "owner" ? (
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        Analyses used: {subscriptionData?.usageCount}/5
                      </div>
                    ) : null}

                    {/* Saved Analyses Clock Icon */}
                    {analysisHistory.length > 0 && (
                      <button
                        onClick={() => {
                          // Check if user is on free plan and should be prompted to upgrade
                          if (
                            subscriptionData?.plan === "free" &&
                            userPermission === "owner"
                          ) {
                            setUpgradeFeatureType("saved_analyses");
                            setShowFeatureUpgradeModal(true);
                          } else {
                            setShowSavedAnalysesModal(true);
                          }
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors relative group"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + 10,
                            text: `View saved analyses (${analysisHistory.length})`,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        <svg
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-3 pt-2 pb-2 flex-1 overflow-hidden">
                  <textarea
                    ref={promptTextareaRef}
                    className="w-full h-full border-none focus:ring-0 resize-none text-base transition-colors duration-200 ease-in-out focus:outline-none disabled:bg-white disabled:opacity-100 overflow-y-auto"
                    placeholder="Enter the essay prompt (e.g., 'Why do you want to attend our college?')"
                    value={prompt}
                    onChange={(e) => handlePromptChange(e.target.value)}
                    disabled={
                      userPermission !== "edit" && userPermission !== "owner"
                    }
                  />
                </div>
                {/* Resize handle for prompt */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 cursor-row-resize hover:bg-blue-500 transition-colors group z-10"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setStartY(e.clientY);
                    setStartHeight(promptHeight);
                    setIsResizingPrompt(true);
                  }}
                >
                  <div className="absolute inset-x-0 -top-1 h-3 flex items-center justify-center">
                    <div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-blue-500 transition-colors"></div>
                  </div>
                </div>
              </div>

              <div
                className="border border-gray-100 rounded-xl shadow-sm overflow-hidden bg-white flex flex-col relative"
                style={{ height: `${editorHeight}px` }}
              >
                <div className="border-b border-gray-100 p-2 flex gap-2 flex-wrap items-center bg-gray-50/50">
                  {userPermission !== "comment" && (
                    <>
                      <button
                        onClick={() =>
                          editor?.chain().focus().toggleBold().run()
                        }
                        disabled={
                          userPermission !== "edit" &&
                          userPermission !== "owner"
                        }
                        className={`editor-toolbar-button ${
                          editor?.isActive("bold")
                            ? "active"
                            : "hover:bg-gray-100"
                        } ${
                          userPermission !== "edit" &&
                          userPermission !== "owner"
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                          <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                        </svg>
                        Bold
                      </button>
                      <button
                        onClick={() =>
                          editor?.chain().focus().toggleItalic().run()
                        }
                        disabled={
                          userPermission !== "edit" &&
                          userPermission !== "owner"
                        }
                        className={`editor-toolbar-button ${
                          editor?.isActive("italic")
                            ? "active"
                            : "hover:bg-gray-100"
                        } ${
                          userPermission !== "edit" &&
                          userPermission !== "owner"
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="19" y1="4" x2="10" y2="4"></line>
                          <line x1="14" y1="20" x2="5" y2="20"></line>
                          <line x1="15" y1="4" x2="9" y2="20"></line>
                        </svg>
                        Italic
                      </button>
                    </>
                  )}
                  <button
                    onClick={addUserComment}
                    disabled={
                      userPermission !== "comment" &&
                      userPermission !== "edit" &&
                      userPermission !== "owner"
                    }
                    title={
                      userPermission === "comment" ||
                      userPermission === "edit" ||
                      userPermission === "owner"
                        ? "Select text first, then click to add a comment"
                        : "You don't have permission to add comments"
                    }
                    className={`editor-toolbar-button ${
                      userPermission !== "comment" &&
                      userPermission !== "edit" &&
                      userPermission !== "owner"
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-blue-50 text-blue-600 border border-blue-200"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    Add Comment
                  </button>
                  <div className="flex-1"></div>

                  {userPermission !== "comment" && (
                    <>
                      {/* Model Selection Dropdown */}
                      <CustomDropdown
                        options={modelOptions}
                        value={selectedModel}
                        onChange={setSelectedModel}
                        disabled={isAnalyzing}
                      />

                      <button
                        onClick={handleAnalyze}
                        disabled={
                          isAnalyzing ||
                          (userPermission !== "edit" &&
                            userPermission !== "owner")
                        }
                        className={`editor-toolbar-button ${
                          isAnalyzing ||
                          (userPermission !== "edit" &&
                            userPermission !== "owner")
                            ? "bg-blue-100 text-blue-400 cursor-not-allowed"
                            : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                        }`}
                      >
                        {isAnalyzing ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                            </svg>
                            Analyze Essay
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <div className="bg-white flex-1 overflow-y-auto p-4">
                  <EditorContent editor={editor} />
                </div>
                <div
                  className={`border-t border-gray-200 bg-gray-50/50 px-3 py-1 text-right text-sm ${
                    wordLimit && wordCount > wordLimit
                      ? "text-red-600 font-medium"
                      : "text-gray-500"
                  }`}
                >
                  {wordCount}
                  {wordLimit ? ` / ${wordLimit}` : ""} words
                  {wordLimit && wordCount > wordLimit && (
                    <span className="ml-2 text-xs">
                      ({wordCount - wordLimit} over limit)
                    </span>
                  )}
                </div>
                {/* Resize handle for editor */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 cursor-row-resize hover:bg-blue-500 transition-colors group z-10"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setStartY(e.clientY);
                    setStartHeight(editorHeight);
                    setIsResizingEditor(true);
                  }}
                >
                  <div className="absolute inset-x-0 -top-1 h-3 flex items-center justify-center">
                    <div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-blue-500 transition-colors"></div>
                  </div>
                </div>
              </div>

              {summary && (
                <div className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-hidden">
                  {/* Header */}
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-gray-600"
                        >
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                      </div>
                      Essay Feedback
                    </h4>
                    <p className="text-sm text-gray-600 mt-1 ml-11">
                      AI-powered analysis of your essay
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <SummarySection summary={summary} />
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="12 6v6l4 2" />
                      </svg>
                      Based on your essay content and prompt
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Connected Right Sidebar */}
            <div
              className={`bg-white flex flex-col relative border border-gray-200 rounded-lg ${
                isResizingSidebar ? "" : "transition-[width] duration-300"
              }`}
              style={{ width: sidebarCollapsed ? "32px" : `${sidebarWidth}%` }}
            >
              {/* Collapsed State - Icon overlay */}
              {sidebarCollapsed && (
                <div
                  className="absolute inset-0 flex items-start justify-center pt-3 cursor-pointer hover:bg-gray-50 transition-colors group/collapse z-20"
                  onClick={() => setSidebarCollapsed(false)}
                >
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  {/* Custom tooltip on the left */}
                  <div className="absolute right-full mr-2 top-3 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/collapse:opacity-100 transition-opacity pointer-events-none">
                    Expand comments/report section
                  </div>
                </div>
              )}

              {/* Resize handle for sidebar */}
              {!sidebarCollapsed && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors group z-10"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setStartX(e.clientX);
                    setStartWidth((sidebarWidth / 100) * window.innerWidth);
                    setIsResizingSidebar(true);
                  }}
                >
                  <div className="absolute inset-y-0 -left-1 w-3 flex items-center justify-center">
                    <div className="h-12 w-1 bg-gray-300 rounded-full group-hover:bg-blue-500 transition-colors"></div>
                  </div>
                </div>
              )}

              {/* Fixed Tab Bar - only show when expanded */}
              {!sidebarCollapsed && (
                <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 min-w-[200px]">
                  <div className="flex">
                    <button
                      onClick={() => setActiveTab("comments")}
                      className={`flex-1 px-4 py-3 text-sm font-medium text-center transition-colors rounded-tl-lg border border-gray-200/60 ${
                        activeTab === "comments"
                          ? "text-blue-600 bg-white border-b-2 border-b-blue-600 -mb-px"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Comments
                    </button>
                    <button
                      onClick={() => setActiveTab("report")}
                      className={`flex-1 px-4 py-3 text-sm font-medium text-center transition-colors border-t border-r border-b border-gray-200/60 ${
                        activeTab === "report"
                          ? "text-blue-600 bg-white border-b-2 border-b-blue-600 -mb-px"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Report
                    </button>
                    <button
                      onClick={() => setSidebarCollapsed(true)}
                      className="relative px-2 py-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors border-t border-r border-b border-gray-200/60 rounded-tr-lg group/collapse-btn"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      {/* Tooltip */}
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/collapse-btn:opacity-100 transition-opacity pointer-events-none z-50">
                        Collapse comments/report section
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Scrollable Content Area - only show when expanded */}
              {!sidebarCollapsed && (
                <div className="overflow-y-auto min-w-[200px]">
                  {/* Tab Content */}
                  {activeTab === "comments" && (
                    <div className="pt-4 px-4 pb-2 space-y-3">
                      {comments.map((comment) => (
                        <div
                          key={comment.id}
                          id={`comment-${comment.id}`}
                          ref={(el) => {
                            sidebarRefs.current[comment.id] = el;
                          }}
                          className={`comment-card rounded-lg p-3 ${
                            comment.type === "ai"
                              ? "bg-blue-50/50 border border-blue-100"
                              : comment.type === "user" &&
                                (comment as UserComment).finalized
                              ? `user-comment-card ${
                                  userPermission === "owner"
                                    ? "owner-comment"
                                    : (comment as UserComment).author ===
                                      user?.email
                                    ? "my-comment"
                                    : "shared-comment"
                                }`
                              : "bg-gray-50/50 border border-gray-100"
                          }`}
                        >
                          {comment.type === "ai" ? (
                            <>
                              <p className="font-medium text-sm mb-2 text-gray-900">
                                "{getCommentedText(comment as AIComment)}"
                              </p>
                              <p className="text-sm text-gray-700">
                                {(comment as AIComment).comment}
                              </p>
                            </>
                          ) : (
                            <>
                              {(comment as UserComment).finalized ? (
                                // Finalized comment - analysis style UI
                                <>
                                  {(comment as UserComment).selectedText && (
                                    <p className="font-medium text-sm mb-2 text-gray-900">
                                      "{(comment as UserComment).selectedText}"
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-700 mb-2">
                                    {(comment as UserComment).text}
                                  </p>
                                  <div className="flex justify-between items-center">
                                    <div className="text-xs text-gray-500">
                                      {(comment as UserComment).author && (
                                        <span className="font-medium">
                                          {getDisplayName(
                                            (comment as UserComment).author ||
                                              ""
                                          )}{" "}
                                          •{" "}
                                        </span>
                                      )}
                                      {formatTimeAgo(
                                        (comment as UserComment).createdAt
                                      )}
                                    </div>
                                    <button
                                      onClick={() => {
                                        console.log(
                                          "DEBUG: Delete button clicked for finalized comment:",
                                          comment.id
                                        );
                                        console.log(
                                          "DEBUG: onSave function available:",
                                          !!onSave
                                        );

                                        // Calculate updated comments without the deleted comment
                                        const updatedComments = comments.filter(
                                          (c) => c.id !== comment.id
                                        );

                                        console.log(
                                          "DEBUG: EssayEditor - Deleting finalized comment:",
                                          comment
                                        );
                                        console.log(
                                          "DEBUG: EssayEditor - updatedComments after deletion:",
                                          updatedComments
                                        );

                                        // Remove UserCommentMark if this is a user comment
                                        if (editor && comment.type === "user") {
                                          // Find and remove the specific mark for this comment ID
                                          const { state } = editor;
                                          const { tr } = state;
                                          let markRemoved = false;

                                          state.doc.descendants((node, pos) => {
                                            node.marks.forEach((mark) => {
                                              if (
                                                mark.type.name ===
                                                  "userCommentMark" &&
                                                mark.attrs.commentId ===
                                                  comment.id
                                              ) {
                                                tr.removeMark(
                                                  pos,
                                                  pos + node.nodeSize,
                                                  mark
                                                );
                                                markRemoved = true;
                                              }
                                            });
                                          });

                                          if (markRemoved) {
                                            editor.view.dispatch(tr);
                                          }
                                        }

                                        // Update state
                                        setComments(updatedComments);

                                        // Force highlight refresh by clearing and re-applying with updated comments
                                        if (editor) {
                                          // Clear all current highlights first
                                          editor.commands.setCommentHighlights(
                                            []
                                          );

                                          // The useEffect will automatically re-apply highlights with the updated comments list
                                          // This happens because setComments triggers the useEffect that depends on [comments, editor, initialContent]
                                        }

                                        // Trigger save to persist the deletion
                                        if (onSave) {
                                          console.log(
                                            "DEBUG: Calling onSave for finalized comment deletion"
                                          );
                                          onSave(
                                            editor?.getHTML() || "",
                                            updatedComments,
                                            summary,
                                            prompt,
                                            title,
                                            report
                                          );
                                        } else {
                                          console.log(
                                            "DEBUG: onSave not available - finalized comment deletion won't persist"
                                          );
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        const rect =
                                          e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                          x: rect.left - 20,
                                          y: rect.bottom + 8,
                                          text: "Delete",
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip(null)}
                                      className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                                    >
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </>
                              ) : (
                                // Unfinalized comment - editable UI
                                <>
                                  {(comment as UserComment).selectedText && (
                                    <div className="bg-gray-50 p-2 rounded mb-2 border-l-4 border-blue-500">
                                      <div className="text-xs text-gray-500 mb-1">
                                        Selected text:
                                      </div>
                                      <div className="text-sm italic text-gray-700">
                                        "{(comment as UserComment).selectedText}
                                        "
                                      </div>
                                    </div>
                                  )}
                                  <textarea
                                    className="w-full p-2 border border-gray-200 rounded-md text-sm mb-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                    placeholder="Enter your comment... (Press Enter to finalize)"
                                    value={(comment as UserComment).text}
                                    onChange={(e) => {
                                      setComments((prevComments) =>
                                        prevComments.map((c) =>
                                          c.id === comment.id
                                            ? { ...c, text: e.target.value }
                                            : c
                                        )
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        e.currentTarget.blur();

                                        // Calculate updated comments with finalized status
                                        const updatedComments = comments.map(
                                          (c) =>
                                            c.id === comment.id &&
                                            c.type === "user"
                                              ? { ...c, finalized: true }
                                              : c
                                        );

                                        console.log(
                                          "DEBUG: EssayEditor - User hit Enter, original comment:",
                                          comment
                                        );
                                        console.log(
                                          "DEBUG: EssayEditor - updatedComments being passed to onSave:",
                                          updatedComments
                                        );

                                        // Mark comment as finalized in state
                                        setComments(updatedComments);

                                        // Trigger save with the updated comments
                                        if (onSave) {
                                          onSave(
                                            editor?.getHTML() || "",
                                            updatedComments,
                                            summary,
                                            prompt,
                                            title,
                                            report
                                          );
                                        }
                                      }
                                    }}
                                    rows={3}
                                  />
                                  <div className="flex justify-between items-center">
                                    <div className="text-xs text-gray-500">
                                      {(comment as UserComment).author && (
                                        <span className="font-medium">
                                          {getDisplayName(
                                            (comment as UserComment).author ||
                                              ""
                                          )}{" "}
                                          •{" "}
                                        </span>
                                      )}
                                      {formatTimeAgo(
                                        (comment as UserComment).createdAt
                                      )}
                                    </div>
                                    <button
                                      onClick={() => {
                                        // Calculate updated comments without the deleted comment
                                        const updatedComments = comments.filter(
                                          (c) => c.id !== comment.id
                                        );

                                        console.log(
                                          "DEBUG: EssayEditor - Deleting comment:",
                                          comment
                                        );
                                        console.log(
                                          "DEBUG: EssayEditor - updatedComments after deletion:",
                                          updatedComments
                                        );

                                        // Remove UserCommentMark if this is a user comment
                                        if (editor && comment.type === "user") {
                                          // Find and remove the specific mark for this comment ID
                                          const { state } = editor;
                                          const { tr } = state;
                                          let markRemoved = false;

                                          state.doc.descendants((node, pos) => {
                                            node.marks.forEach((mark) => {
                                              if (
                                                mark.type.name ===
                                                  "userCommentMark" &&
                                                mark.attrs.commentId ===
                                                  comment.id
                                              ) {
                                                tr.removeMark(
                                                  pos,
                                                  pos + node.nodeSize,
                                                  mark
                                                );
                                                markRemoved = true;
                                              }
                                            });
                                          });

                                          if (markRemoved) {
                                            editor.view.dispatch(tr);
                                          }
                                        }

                                        // Update state
                                        setComments(updatedComments);

                                        // Force highlight refresh by clearing and re-applying with updated comments
                                        if (editor) {
                                          // Clear all current highlights first
                                          editor.commands.setCommentHighlights(
                                            []
                                          );

                                          // The useEffect will automatically re-apply highlights with the updated comments list
                                          // This happens because setComments triggers the useEffect that depends on [comments, editor, initialContent]
                                        }

                                        // Trigger save to persist the deletion
                                        if (onSave) {
                                          onSave(
                                            editor?.getHTML() || "",
                                            updatedComments,
                                            summary,
                                            prompt,
                                            title,
                                            report
                                          );
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        const rect =
                                          e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                          x: rect.left - 20,
                                          y: rect.bottom + 8,
                                          text: "Delete",
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip(null)}
                                      className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                      {!comments.length && (
                        <p className="text-gray-600 text-sm whitespace-nowrap">
                          Analyze your essay to get comments.
                        </p>
                      )}
                    </div>
                  )}

                  {activeTab === "report" && (
                    <div className="pt-4 px-4 pb-2">
                      {report ? (
                        <ScoreReportDisplay report={report} />
                      ) : (
                        <p className="text-gray-600 text-sm whitespace-nowrap">
                          Analyze your essay to get a report.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeMainTab === "ideas" && (
          <IdeaCreation
            prompt={prompt}
            onPromptChange={handlePromptChange}
            disabled={isAnalyzing}
            userId={user?.id}
            suggestions={suggestions}
            onSuggestionsChange={setSuggestions}
            tips={promptTips}
            tipsLoading={promptTipsLoading}
            tipsError={promptTipsError}
            onTipsChange={setPromptTips}
            chatOpen={chatOpen}
            chatMessages={chatMessages}
            chatInput={chatInput}
            chatLoading={chatLoading}
            onOpenChat={handleOpenChat}
            onCloseChat={handleCloseChat}
            onSendMessage={handleSendMessage}
            onChatInputChange={setChatInput}
          />
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && userPermission === "owner" && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowShareModal(false);
            setShareEmails([]);
            setShareEmailInput("");
            setSharePermission("comment");
          }}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16,6 12,2 8,6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Share Essay
                  </h3>
                  <p className="text-sm text-gray-500">
                    Give others access to your essay
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setShareEmails([]);
                  setShareEmailInput("");
                  setSharePermission("comment");
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/*
            <div className="space-y-6">
              {/* Email Input Section */}
            {/*
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <span className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Invite people
                  </span>
                </label>

                {/* Email Tags */}
            {/*
                {shareEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {shareEmails.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        <span>{email}</span>
                        <button
                          onClick={() => removeEmail(email)}
                          className="text-gray-500 hover:text-gray-700 ml-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Email Input */}
            {/*
                <div className="relative">
                  <input
                    type="email"
                    value={shareEmailInput}
                    onChange={(e) => setShareEmailInput(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    onBlur={() => {
                      if (shareEmailInput.trim()) {
                        addEmail(shareEmailInput);
                      }
                    }}
                    placeholder="Enter email address and press Enter"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm placeholder-gray-400"
                  />
                  {shareEmailInput && (
                    <button
                      onClick={() => addEmail(shareEmailInput)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Add email addresses one by one. Press Enter or click + to add
                  each email.
                </p>
              </div>

              {/* Permission Level */}
            {/*
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <span className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="11"
                        rx="2"
                        ry="2"
                      ></rect>
                      <circle cx="12" cy="16" r="1"></circle>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    Access level
                  </span>
                </label>

                <div className="grid grid-cols-1 gap-2">
                  {[
                    {
                      value: "read",
                      label: "Read only",
                      description: "Can view the essay",
                      icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      ),
                    },
                    {
                      value: "comment",
                      label: "Can comment",
                      description: "Can view and add comments",
                      icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                      ),
                    },
                    {
                      value: "edit",
                      label: "Can edit",
                      description: "Can view, comment, and edit",
                      icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      ),
                    },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        sharePermission === option.value
                          ? "border-gray-900 bg-gray-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="permission"
                        value={option.value}
                        checked={sharePermission === option.value}
                        onChange={(e) =>
                          setSharePermission(
                            e.target.value as "read" | "comment" | "edit"
                          )
                        }
                        className="sr-only"
                      />
                      <div
                        className={`flex items-center justify-center w-5 h-5 rounded-full border-2 mr-3 ${
                          sharePermission === option.value
                            ? "border-gray-900 bg-gray-900"
                            : "border-gray-300"
                        }`}
                      >
                        {sharePermission === option.value && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-gray-600">{option.icon}</div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {option.label}
                          </div>
                          <div className="text-sm text-gray-500">
                            {option.description}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            */}

            {/* Public Sharing Section */}
            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      Public Sharing
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Anyone with this link can view your essay (read-only)
                  </p>
                </div>

                {/* Toggle Switch */}
                <button
                  onClick={handleTogglePublicSharing}
                  disabled={isLoadingPublicLink || isTogglingShare}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
                    isLoadingPublicLink || isTogglingShare
                      ? "bg-gray-300" // Disabled state
                      : publicShareLink?.is_active
                      ? "bg-indigo-600"
                      : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                      isLoadingPublicLink || isTogglingShare
                        ? "bg-gray-100" // Disabled state
                        : "bg-white"
                    } ${
                      publicShareLink?.is_active
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Share Link Status Container - Fixed height to prevent modal resizing */}
              {publicShareLink && (
                <div
                  className={`mt-4 p-3 rounded-lg border transition-all duration-200 min-h-[90px] flex items-center justify-center ${
                    publicShareLink.is_active
                      ? "bg-gray-50 border-gray-200"
                      : "bg-gray-100 border-gray-300"
                  }`}
                >
                  {publicShareLink.is_active ? (
                    // Active State - Show link details
                    <div className="w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-3">
                          <div className="flex items-center gap-3">
                            <div className="text-sm text-gray-900 font-mono">
                              campusquest.io/share/...
                            </div>
                            <button
                              onClick={copyPublicLinkToClipboard}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors flex items-center gap-1.5"
                              title="Copy full link"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                              <span className="text-xs">Copy</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                            <span>{publicShareLink.view_count} views</span>
                            <span>
                              Created{" "}
                              {new Date(
                                publicShareLink.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Inactive State - Show disabled message
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        Public sharing is disabled
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Toggle the switch above to enable sharing
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Existing Shares Section */}
            {existingShares.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium pt-4 text-gray-700 mb-3">
                  <span className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Currently shared with
                  </span>
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {existingShares.map((share) => (
                    <div
                      key={share.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={
                            share.shared_user?.profile_image_url ||
                            `https://avatar.vercel.sh/${
                              share.shared_user?.email ||
                              share.shared_with_email
                            }.png`
                          }
                          alt={
                            share.shared_user?.name || share.shared_with_email
                          }
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {share.shared_user?.name ||
                              share.shared_user?.email ||
                              share.shared_with_email}
                          </div>
                          <div className="text-xs text-gray-500">
                            {share.shared_user?.email ||
                              share.shared_with_email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={
                            updatedPermissions[share.id] ||
                            share.permission_level
                          }
                          onChange={(e) =>
                            handlePermissionChange(
                              share.id,
                              e.target.value as "read" | "comment" | "edit"
                            )
                          }
                          className="px-2 py-1 rounded-md text-xs font-medium border-gray-300 focus:ring-gray-500 focus:border-gray-500"
                        >
                          <option value="read">Read</option>
                          <option value="comment">Comment</option>
                          <option value="edit">Edit</option>
                        </select>
                        <button
                          onClick={() => handleRemoveShare(share.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          title="Remove access"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setShareEmails([]);
                  setShareEmailInput("");
                  setSharePermission("comment");
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium"
              >
                Close
              </button>
              {/* Save Changes button - only show if there are permission updates */}
              {Object.keys(updatedPermissions).length > 0 && (
                <button
                  onClick={handleShareOrSave}
                  className="px-6 py-2 text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17,21 17,13 7,13 7,21"></polyline>
                    <polyline points="7,3 7,8 15,8"></polyline>
                  </svg>
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-indigo-600"
                >
                  <path d="M12 1v6m0 0l4-4m-4 4L8 3m5 9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Analysis Limit Reached
              </h3>
              <p className="text-gray-600 mb-6">
                You've used all 5 of your free analyses. Upgrade to Pro for
                unlimited essay analyses and advanced features.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    handleUpgradeClick();
                  }}
                  className="px-4 py-2 text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium"
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-600"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Save Your Progress
              </h3>
              <p className="text-gray-600 mb-6">
                Upgrade to Pro to save your essays, access analysis history, and
                never lose your work.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    handleUpgradeClick();
                  }}
                  className="px-4 py-2 text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-lg transition-all duration-200 font-medium"
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Upgrade Modal */}
      {showFeatureUpgradeModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
            upgradeFeatureType === "ideation" ? "backdrop-blur-sm" : ""
          }`}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl transition-all duration-300">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                {upgradeFeatureType === "saved_analyses" ? (
                  <svg
                    className="w-6 h-6 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {upgradeFeatureType === "saved_analyses"
                  ? "Unlock Analysis History"
                  : "Unlock AI Ideation"}
              </h3>
              <p className="text-gray-600 mb-6">
                {upgradeFeatureType === "saved_analyses"
                  ? "Access your saved analyses and compare different versions of your feedback. Upgrade to Pro to view your analysis history."
                  : "Generate AI-powered ideas and brainstorming suggestions for your essays. Upgrade to Pro to unlock the ideation tools."}
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={closeFeatureUpgradeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    closeFeatureUpgradeModal();
                    handleUpgradeClick();
                  }}
                  className="px-4 py-2 text-white bg-black hover:bg-gray-800 rounded-lg transition-all duration-200 font-medium"
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Analysis
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this analysis? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setAnalysisToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Analyses Modal */}
      {showSavedAnalysesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Saved Analyses
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose an analysis to load ({analysisHistory.length} saved)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSavedAnalysesModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Analyses List */}
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-3">
                {[...analysisHistory].reverse().map((analysis, index) => {
                  // Calculate the original index for proper naming
                  const originalIndex = analysisHistory.length - 1 - index;
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        // Load the selected analysis
                        setComments(analysis.comments || []);
                        setSummary(analysis.summary || "");
                        setReport(analysis.report || null);
                        setActiveTab("comments");
                        setShowSavedAnalysesModal(false);
                      }}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-gray-900 group-hover:text-blue-700">
                              {analysis.name || `Analysis ${originalIndex + 1}`}
                            </h4>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              Analysis
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {new Date(analysis.timestamp).toLocaleString()}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-4.906-1.447l-3.166 1.062a1.125 1.125 0 01-1.443-1.443l1.062-3.166A8.955 8.955 0 011 12c0-4.418 3.582-8 8-8s8 3.582 8 8z"
                                />
                              </svg>
                              {analysis.comments?.length || 0} comments
                            </span>
                            {analysis.report && (
                              <span className="flex items-center gap-1">
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                  />
                                </svg>
                                Score: {analysis.report.overall_score}/100
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAnalysis?.(index);
                            setShowSavedAnalysesModal(false);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors ml-2"
                          title="Delete this analysis"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
              <button
                onClick={() => setShowSavedAnalysesModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {tooltip && (
        <div
          className="fixed z-[9999] bg-gray-800 text-white text-sm font-semibold rounded-lg px-4 py-3 shadow-xl pointer-events-none transition-opacity duration-75 ease-out"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            maxWidth: "320px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Essay Settings Modal */}
      <EssaySettingsModal
        open={showSettingsModal}
        onOpenChange={setShowSettingsModal}
        onSave={handleSaveEssaySettings}
        currentPrompt={prompt}
        currentCollegeName={collegeName}
        essayId={essayId}
      />
    </div>
  );
};

export default EssayEditor;
